import React, { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react";

const BrowserSecurity = () => {
  const navigate = useNavigate();
  // Reference for the timeline section to track scroll progress
  const timelineRef = useRef(null);

  // Captures scroll progress specifically within this container
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  // Transforms the scroll progress into a height scale percentage (from 0% to 100%)
  const scaleY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const tabsData = [
    {
      id: "tab1",
      title: "Browser-Native Security",
      contentTitle: "Browser-Native Security",
      description:
        "Secure modern browser activity across SaaS, AI platforms, unmanaged devices, shadow IT, and identity-driven threats.",
    },
    {
      id: "tab2",
      title: "User Experience",
      contentTitle: "User Experience",
      description:
        "Security strategies that strengthen protection while maintaining seamless user experience, workforce mobility, and business agility across distributed environments.",
    },
    {
      id: "tab3",
      title: "Deep Visibility",
      contentTitle: "Deep Visibility",
      description:
        "Gain actionable visibility into browser behavior, SaaS interactions, AI tool usage, unmanaged access, risky user activity, and sensitive data movement.",
    },
    {
      id: "tab4",
      title: "Vendor-Neutral Advisory",
      contentTitle: "Vendor-Neutral Advisory",
      description:
        "Independent, vendor-neutral guidance to help organizations select architectures and technologies aligned to their operational, security, and business requirements.",
    },
    {
      id: "tab5",
      title: "Operational Resilience",
      contentTitle: "Operational Resilience",
      description:
        "Operationalize browser security through structured governance, policy alignment, risk reduction strategies, phased implementation models, and continuous optimization.",
    },
  ];

  const [activeTab, setActiveTab] = useState("tab1");

  const timelineData = [
    {
      title: "Business & User Risk Alignment",
      description:
        "Align security, compliance, IT, and business stakeholders around browser security objectives, acceptable usage policies, data protection requirements, and workforce enablement goals.",
    },
    {
      title: "Browser & SaaS Visibility Assessment",
      description:
        "Assess browser usage patterns, SaaS adoption, unmanaged access, extension usage, shadow IT exposure, and user interaction with AI and cloud services across the enterprise.",
    },
    {
      title: "Threat & Data Exposure Analysis",
      description:
        "Identify risks related to phishing, session hijacking, malicious extensions, credential compromise, unsafe browsing behavior, data leakage, and unauthorized access to sensitive applications and data.",
    },
    {
      title: "Identity & Access Security Review",
      description:
        "Evaluate browser-based authentication flows, session management, identity integrations, conditional access policies, and Zero Trust controls supporting secure user access.",
    },
    {
      title: "Browser Security Architecture & Policy Design",
      description:
        "Define browser security frameworks, policy controls, isolation strategies, DLP enforcement, AI usage governance, extension management, and secure access requirements aligned to business and compliance needs.",
    },
    {
      title: "Deployment & Security Control Implementation",
      description:
        "Deploy browser security capabilities using structured implementation methodologies tailored to your environment. Configure policy enforcement, monitoring, secure browsing protections, and visibility controls while maintaining user experience and operational continuity.",
    },
    {
      title: "Visibility, Governance & Continuous Optimization",
      description:
        "Enable centralized monitoring, policy governance, user activity visibility, and continuous optimization of browser security posture to support evolving workforce, SaaS, and AI adoption requirements.",
    },
  ];

  return (
    <div className="bg-[#011916] text-white min-h-screen font-sans overflow-x-hidden selection:bg-[#048649] selection:text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-20 left-8 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-[#24ff81] transition-all duration-300"
      >
        <ArrowLeft size={20} />
      </button>

      {/* SECTION 1: TOP HERO HEADER SECTION */}
      <ScrollReveal>
        <div className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto">
          <section className="relative bg-[#022722] rounded-[3rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* LEFT CONTENT */}
            <div className="md:col-span-6">
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
                Browser Security
              </h1>

              <h4 className="text-white text-lg md:text-xl font-extrabold mb-5 tracking-wide uppercase">
                Securing the Modern Browser-Driven Enterprise
              </h4>

              <p className="text-gray-300 text-base md:text-lg leading-[1.8] mb-10 max-w-2xl">
                The web browser has become the primary workspace for today’s
                hybrid workforce serving as the gateway to SaaS applications,
                cloud platforms, corporate data, and AI-driven services.
                Traditional security controls often lack visibility and control
                at the browser layer, exposing organizations to phishing, data
                leakage, malicious extensions, shadow IT, and unmanaged device
                risks.
                <br />
                <br />
                Synthera Security’s Browser Security Service helps enterprises
                strengthen browser-layer protection, improve visibility, and
                enable secure access to business-critical applications, cloud
                services, and AI platforms across managed and unmanaged
                environments.
              </p>

              <Link
                to="/contact"
                className="bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3 rounded-md transition-all duration-200 w-fit no-underline shadow-lg shadow-green-950/20"
              >
                Work with Us
              </Link>
            </div>

            {/* RIGHT IMAGE */}
            <div className="md:col-span-6 flex justify-center items-center w-full">
              <div className="w-[85%] rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214429/browser_s_ie8emz.jpg"
                  alt="Browser Security Visual"
                  className="w-full h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>
        </div>
      </ScrollReveal>

      {/* SECTION 2: EXPERIENCE THE DIFFERENCE (TAB SELECTOR) */}
      <ScrollReveal>
        <section className="relative bg-[#011916] py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center md:text-left mb-12">
            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider block mb-1">
              Why Choose Synthera Security?
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-gray-300 text-xs md:text-sm mt-2">
              Synthera Security helps organizations secure the modern
              browser-driven enterprise through a strategic, business-aligned
              approach that addresses the growing risks introduced by SaaS
              adoption, hybrid work, unmanaged devices, and AI-powered
              workflows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Button Controls Menu */}
            <div className="md:col-span-5 flex flex-col gap-2">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-5 py-3.5 rounded-lg border text-xs md:text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-[#04433c] text-emerald-300 border-emerald-500/30 shadow-md"
                      : "bg-[#021f1b] text-gray-400 border-white/5 hover:bg-[#022722] hover:text-white"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Right Display Presentation Panel */}
            <div className="md:col-span-7 h-full min-h-[200px]">
              <div className="bg-[#021f1b] border border-white/5 rounded-xl p-8 h-full flex flex-col justify-center relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  {tabsData.map(
                    (tab) =>
                      tab.id === activeTab && (
                        <motion.div
                          key={tab.id}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.15 }}
                        >
                          <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                            {tab.contentTitle}
                          </h3>
                          <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                            {tab.description}
                          </p>
                        </motion.div>
                      ),
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 3: MID-PAGE EXPOSITION */}
      <ScrollReveal>
        <section className="bg-[#011916] py-16 px-6 text-center border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Approach
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white tracking-tight uppercase mb-4">
              Beyond Traditional Endpoint Security
            </h2>
            <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
              Modern browser activity has become a primary access point for SaaS
              applications, cloud workloads, AI platforms, and sensitive
              enterprise data, limiting the effectiveness of traditional
              security controls. Synthera Security delivers a holistic browser
              security approach covering user behavior, SaaS interactions,
              identity controls, extension risks, and unmanaged access patterns
              to help organizations build secure, resilient, and
              productivity-focused browser-driven work environments with
              enhanced visibility and control.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 4: DYNAMIC TIMELINE / METHODOLOGY */}
      <ScrollReveal>
        <section
          ref={timelineRef}
          className="bg-[#031513] pb-32 pt-6 px-6 max-w-4xl mx-auto relative rounded-2xl"
        >
          <div className="mb-16 text-center md:text-left">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Synthera Methodology
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Process & Implementation
            </h2>
          </div>

          {/* Timeline Wrapper Grid Layout */}
          <div className="relative pl-6 md:pl-10">
            {/* Background Base Muted Track Line */}
            <div className="absolute left-1.5 md:left-3 top-2 bottom-2 w-[1px] bg-[#021f1b]" />

            {/* DYNAMIC SCROLLING GREEN ACCENT LINE STEM */}
            <motion.div
              style={{ height: scaleY }}
              className="absolute left-1.5 md:left-3 top-2 w-[1px] bg-emerald-500 origin-top z-10 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            />

            <div className="flex flex-col gap-12 relative">
              {timelineData.map((item, index) => (
                <div key={index} className="w-full relative group">
                  {/* Dynamic Glowing Green Circle Node Marker */}
                  <motion.div
                    initial={{ scale: 0.6, backgroundColor: "#021f1b" }}
                    whileInView={{ scale: 1, backgroundColor: "#048649" }}
                    viewport={{ once: false, amount: "even" }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-[23px] md:-left-[35px] w-2.5 h-2.5 top-1.5 rounded-full z-20 ring-4 ring-[#032d28]"
                  />

                  {/* Dynamic Text Group revealing on Scroll viewport arrival */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="text-base md:text-lg font-bold text-white mb-2 tracking-tight transition-colors duration-300 group-hover:text-emerald-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl font-light">
                      {item.description}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
      <Footer />
    </div>
  );
};

export default BrowserSecurity;
